import { isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Inject, Input, OnInit, Output, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OffersFilters } from '@app/models/filtermodels/OffersFilters';
import { ParamFilters } from '@app/models/filtermodels/ParamFilters';
import { OffersResponse } from '@app/models/offers/OffersResponse';
import { Partner } from '@app/models/partner/IPartner';
import { AuthGuardService } from '@app/services/auth-guard.service';
import { HotelFiltersService } from '@app/services/hotelfilters.service';
import { TrafficsService } from '@app/services/traffics.service';
import moment from 'moment';
import { MediaService } from '@app/services/media.service';

@Component({
    selector: 'clever-flugextrafilter',
    templateUrl: './flugfilter.component.html',
    styleUrls: ['./flugfilter.component.scss'],
    standalone: false
})
export class FlugExtrafilterComponent implements OnInit {
    @Input() mode: string;
    @Input() type: string;
    @Output() closeModal: EventEmitter<boolean> = new EventEmitter();
    filtersStaticData: OffersFilters;
    tmp_filtersStaticData: OffersFilters;
    isBrowser = signal(false);
    loading = signal(false);
    media: string = 'lg';
    realparams: ParamFilters;
    tourOperatorList = signal(null);
    partner = signal<Partner>(null);

    destroyRef = inject(DestroyRef);
    tmp_stat = signal<OffersResponse>(null);

    constructor(
        protected mediaService: MediaService,
        protected trafficsService: TrafficsService,
        protected guardService: AuthGuardService,
        protected hotelService: HotelFiltersService,
        @Inject(PLATFORM_ID) protected platformId: any,
    ) {
        this.isBrowser.set(isPlatformBrowser(this.platformId));
    }

    ngOnInit(): void {
        if (this.isBrowser()) {
            this.initPartnerSub();
        }
    }

    initPartnerSub(): void {
        this.guardService.getPartner$.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(partner => {
            if (partner?.partnerid?.length > 0) {
                this.partner.set(partner);
                this.initFilters();
            }
        });
    }

    initFilters(): void {
        this.hotelService.getfilters$.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(params => {
            if (params && JSON.stringify(params) !== JSON.stringify(this.realparams)) {
                this.realparams = JSON.parse(JSON.stringify(params));
                this.initFilter();
            }
        });
    }

    closeextras() {
        this.closeModal.emit(true);
    }

    async resetAll() {
        this.hotelService.resetAllExtrasHome();
    }

    initFilter(): void {
        this.filtersStaticData = {} as OffersFilters;
        this.filtersStaticData.productType = this.realparams.productType;
        this.filtersStaticData.productSubType = 'all';
        this.initPrice();
        this.initDates()
        this.initTime();
        this.initAirports();
        this.initReisende();
        this.getStaticData();
        this.initArrivalAirports();
        this.initFlug();
    }

    protected initPrice(): void {
        if (this.realparams.maxPricePerPerson) {
            this.filtersStaticData.maxPricePerPerson = Number(this.realparams.maxPricePerPerson);
        } else {
            this.filtersStaticData.maxPricePerPerson = null;
        }

        if (this.realparams.minPricePerPerson) {
            this.filtersStaticData.minPricePerPerson = Number(this.realparams.minPricePerPerson);
        } else {
            this.filtersStaticData.minPricePerPerson = null;
        }
    }

    protected initDates(): void {
        const reg = new RegExp('^\\d+,\\d+,\\d+$');
        if (String(this.realparams.reisezeitraumResult)?.match(reg)) {
            this.filtersStaticData.searchDate = this.realparams.reisezeitraumResult;
        } else {
            if (this.realparams?.reisezeitraumResult?.start && this.realparams?.reisezeitraumResult?.end) {
                if (!isNaN(Number(this.realparams?.reisezeitraumResult?.start))) {
                    this.realparams.reisezeitraumResult.start = moment().add(Number(this.realparams.reisezeitraumResult.start), 'days').format('YYYY-MM-DD');
                    this.realparams.reisezeitraumResult.end = moment().add(Number(this.realparams.reisezeitraumResult.end), 'days').format('YYYY-MM-DD');
                } else {
                    this.realparams.reisezeitraumResult.start = moment(this.realparams.reisezeitraumResult.start).format('YYYY-MM-DD');
                    this.realparams.reisezeitraumResult.end = moment(this.realparams.reisezeitraumResult.end).format('YYYY-MM-DD');
                }
            } else {
                if (!this.realparams?.reisezeitraumResult?.minDuration) {
                    if (!this.realparams.reisezeitraumResult) {
                        this.realparams.reisezeitraumResult = {
                            start: moment().add(4, 'days').format('YYYY-MM-DD'),
                            end: moment().add(180, 'days').format('YYYY-MM-DD'),
                            minDuration: 7,
                            maxDuration: 7
                        };
                    } else {
                        this.realparams.reisezeitraumResult.minDuration = this.realparams?.reisedauerResult?.value;
                    }
                }
                if (moment(this.realparams?.reisezeitraumResult?.start) < moment()) {
                    this.realparams.reisezeitraumResult.start = moment().format('YYYY-MM-DD');
                }
                if (moment(this.realparams?.reisezeitraumResult?.end) < moment()) {
                    this.realparams.reisezeitraumResult.end = moment(this.realparams.reisezeitraumResult.start)
                        .add(7, 'days').format('YYYY-MM-DD');
                }
            }
            this.filtersStaticData.fromDate = this.realparams?.reisezeitraumResult?.start;
            this.filtersStaticData.toDate = this.realparams?.reisezeitraumResult?.end;
            if (this.realparams?.reisezeitraumResult?.minDuration === '-1') {
                this.filtersStaticData.minDuration = moment.duration(moment(this.realparams?.reisezeitraumResult?.end).diff(this.realparams?.reisezeitraumResult?.start)).asDays().toFixed(0);
                this.filtersStaticData.maxDuration = this.filtersStaticData.minDuration;
            } else {
                this.filtersStaticData.minDuration = this.realparams?.reisezeitraumResult?.minDuration;
                this.filtersStaticData.maxDuration = this.realparams?.reisezeitraumResult?.maxDuration ? this.realparams?.reisezeitraumResult?.maxDuration : this.realparams?.reisezeitraumResult?.minDuration;
            }
            this.filtersStaticData.duration = ',,' + this.filtersStaticData.minDuration + ',' + this.filtersStaticData.maxDuration;
        }
    }

    protected initAirports(): void {
        this.filtersStaticData.departureAirportList = '';
        if (this.realparams.airportsResult) {
            if (this.realparams.airportsResult && this.realparams.airportsResult.length > 0) {
                for (let i = 0; i < this.realparams.airportsResult.length; i++) {
                    this.filtersStaticData.departureAirportList += '' + this.realparams.airportsResult[i].code;
                    if (i < this.realparams.airportsResult.length - 1) {
                        this.filtersStaticData.departureAirportList += ',';
                    }
                }
            }
        }
    }

    protected initArrivalAirports(): void {
        this.filtersStaticData.arrivalAirportList = '';
        if (this.realparams.arrivalAirportList) {
            if (this.realparams.arrivalAirportList && this.realparams.arrivalAirportList.length > 0) {
                for (let i = 0; i < this.realparams.arrivalAirportList.length; i++) {
                    this.filtersStaticData.arrivalAirportList += '' + this.realparams.arrivalAirportList[i].code;
                    if (i < this.realparams.arrivalAirportList.length - 1) {
                        this.filtersStaticData.arrivalAirportList += ',';
                    }
                }
            }
        }
    }

    protected initTime() {
        this.filtersStaticData.minReturnTime = '';
        this.filtersStaticData.maxReturnTime = '';
        this.filtersStaticData.minDepartureTime = '';
        this.filtersStaticData.maxDepartureTime = '';
        if (this.realparams.maxreturnTime > 0 && this.realparams.maxreturnTime < 1440) {
            this.filtersStaticData.maxReturnTime = this.transformMinute(this.realparams.maxreturnTime);
        }

        if (this.realparams.minreturnTime > 0 && this.realparams.minreturnTime < 1440) {
            this.filtersStaticData.minReturnTime = this.transformMinute(this.realparams.minreturnTime);
        }

        if (this.realparams.maxdepartureTime > 0 && this.realparams.maxdepartureTime < 1440) {
            this.filtersStaticData.maxDepartureTime = this.transformMinute(this.realparams.maxdepartureTime);
        }

        if (this.realparams.mindepartureTime > 0 && this.realparams.mindepartureTime < 1440) {
            this.filtersStaticData.minDepartureTime = this.transformMinute(this.realparams.mindepartureTime);
        }
    }

    transformMinute(value: number): string {
        const hours = ('00' + Math.floor(value / 60)).slice(-2);
        const minutes = ('00' + Math.floor(value % 60)).slice(-2);
        return hours + ':' + minutes;
    }

    protected initReisende(): void {
        if (this.realparams.reisendeResult) {
            this.filtersStaticData.adults = '' + this.realparams.reisendeResult.adults;
            this.filtersStaticData.children = '';
            if (this.realparams.reisendeResult && this.realparams.reisendeResult.children &&
                this.realparams.reisendeResult.children.length > 0) {
                for (let i = 0; i < this.realparams.reisendeResult.children.length; i++) {
                    this.filtersStaticData.children += this.realparams.reisendeResult.children[i].age;
                    if (i < this.realparams.reisendeResult.children.length - 1) {
                        this.filtersStaticData.children += ',';
                    }
                }
            }
        }
    }

    protected initFlug(): void {
        this.filtersStaticData.flight = {};
        this.filtersStaticData.returnFlight = {};

        // direktflug
        this.filtersStaticData.flight.stopover = this.realparams?.flight?.stopover;
        this.filtersStaticData.flight.directness = this.realparams?.flight?.directness;
        this.filtersStaticData.flight.tariff = this.realparams?.flight?.tariff;
        this.filtersStaticData.flight.bookingClass = this.realparams?.flight?.bookingClass;

        this.filtersStaticData.returnFlight = this.filtersStaticData.flight;
    }

    protected getStaticData() {
        this.loading.set(true);
        if (JSON.stringify(this.filtersStaticData) !== JSON.stringify(this.tmp_filtersStaticData)) {
            this.tmp_filtersStaticData = JSON.parse(JSON.stringify(this.filtersStaticData));
            this.filtersStaticData.optionLists = 'flightCarrier,tourOperator';
            this.trafficsService.request(this.filtersStaticData, 'offers').pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(obj => {
                this.tmp_stat.set(obj);
                this.tourOperatorList.set(obj?.tourOperatorList);
                this.loading.set(false);
            });
        }
    }
}
